import type { LibraryAssetContent } from "@/api/library";

/**
 * The editable projection of a parts-shaped library asset, and the rules the
 * browser can enforce about it without duplicating the server.
 *
 * A stored part is `{key, kind, text}` or `{key, kind, data: JsonValue}`. Neither
 * is a thing a clinician can type INTO: half-typed JSON is not a `JsonValue`, so
 * an editor that stored `data` directly would have to destroy or reject the
 * buffer on every keystroke that left it momentarily unparseable. A draft part
 * therefore carries one text buffer for both kinds — the JSON source for a
 * structured part — and is converted back only when the clinician saves.
 *
 * WHAT IS CHECKED HERE, AND WHAT DELIBERATELY IS NOT.
 *
 * Everything below is a shape rule stated in `schemas/library.py` on the wire
 * models themselves — the key pattern, key uniqueness, the non-empty prompt, the
 * envelope's part count and byte ceiling. Those are knowable to the browser and
 * attributable to an exact part, so catching them before the request is a better
 * message, not a second opinion.
 *
 * The per-type PART MANIFEST is not checked here and must not be. It lives in
 * `library.py` as a service-layer dict precisely so it can move as the pack tree
 * evolves without a contract change, which means the generated client cannot see
 * it. A copy here would be a second manifest, free to drift from the one that
 * actually decides, and drift in this direction is the worst kind: the editor
 * would refuse content the server accepts, or promise content it refuses. The
 * server's field-pointed 422 stays the authority; `attributePartsFailure` below
 * only decides which part on screen the answer belongs to.
 */

export type PartKind = "prompt_text" | "structured";

export interface DraftPart {
  /**
   * Identity for React and for reorder, distinct from `key`.
   *
   * The clinician edits `key`, and two parts are legitimately keyless-equal
   * mid-edit (a blank new part beside another blank new part). Keying the list
   * on the clinician's own text would make React reuse the wrong row's DOM the
   * moment two keys collided — including the focused control.
   */
  id: string;
  key: string;
  kind: PartKind;
  /** What the clinician typed. JSON source when `kind` is `structured`. */
  text: string;
}

/** Mirrors the contract's `PartKey`: an optional language prefix, then a slug. */
export const PART_KEY_PATTERN = /^(?:[a-z]{2}(?:-[A-Z]{2})?\/)?[a-z][a-z0-9_]{0,63}$/;

/** `PromptPart.text` bounds, from the contract. */
const MAX_PROMPT_CHARS = 100_000;
/** `PartsContent.parts` bounds, from the contract. */
const MAX_PARTS = 64;
const MIN_PARTS = 1;
/** `MAX_STRUCTURED_BYTES`, the envelope-wide ceiling in `schemas/library.py`. */
export const MAX_CONTENT_BYTES = 1_048_576;

let nextDraftId = 0;

/**
 * One fresh `DraftPart.id`, from the counter this module already owns.
 *
 * Exported so a caller that builds parts itself — `namedParts.addNamedRole` and
 * `namedParts.addRequiredLanguage` take a `nextId` — draws from the SAME
 * sequence `toDraft` and `newDraftPart` do, rather than starting a second
 * counter that could hand two rows one id. The counter is module-scoped and
 * monotonic, which is all the identity a React key needs.
 */
export function newDraftId(): string {
  nextDraftId += 1;
  return `draft-part-${nextDraftId}`;
}

export function isPartsContent(
  content: LibraryAssetContent,
): content is Extract<LibraryAssetContent, { kind: "parts" }> {
  return content.kind === "parts";
}

/**
 * `he/screening_prompt` -> `["he", "screening_prompt"]`; `domains` -> `[null, "domains"]`.
 *
 * A bare key is language-NEUTRAL rather than a defaulted language, matching
 * `library.split_part_key` — some pack files genuinely sit outside the
 * per-language tree.
 */
export function splitPartKey(key: string): [string | null, string] {
  const matched = /^([a-z]{2}(?:-[A-Z]{2})?)\/(.+)$/.exec(key);
  return matched ? [matched[1] as string, matched[2] as string] : [null, key];
}

export function partKindLabel(kind: PartKind): string {
  return kind === "prompt_text" ? "Prompt" : "Structured";
}

/**
 * The stored content, opened for editing.
 *
 * Structured data is pretty-printed rather than served compact: this is the
 * buffer a clinician reads and edits, and `{"steps":["greet"]}` on one line is
 * not a document. Re-serialising on save drops the indentation again, so the
 * canonical form the server hashes is unaffected.
 */
export function toDraft(
  content: Extract<LibraryAssetContent, { kind: "parts" }>,
): DraftPart[] {
  return content.parts.map((part) => ({
    id: newDraftId(),
    key: part.key,
    kind: part.kind,
    text:
      part.kind === "prompt_text"
        ? part.text
        : JSON.stringify(part.data, null, 2),
  }));
}

export function newDraftPart(kind: PartKind): DraftPart {
  return {
    id: newDraftId(),
    key: "",
    kind,
    // A structured part opens on the empty object rather than an empty buffer:
    // "" is not JSON, so a part added and left untouched would be a validation
    // failure the clinician did not cause.
    text: kind === "structured" ? "{}" : "",
  };
}

/**
 * One refusal, attributable.
 *
 * `index` is a position in the draft, or `null` when the problem belongs to the
 * set rather than to any one part — an empty parts list, or a total size over
 * the envelope ceiling. Closing #33's "Lovable gap" means the first case is the
 * common one: a refusal that names "content" when it could have named a part is
 * the message this ticket exists to replace.
 */
export interface PartProblem {
  index: number | null;
  message: string;
}

/**
 * The byte length the server will measure.
 *
 * `JSON.stringify` with no spacing matches `json.dumps(separators=(",", ":"))`,
 * and neither escapes non-ASCII, so the two agree on the clinical text that
 * actually fills this budget. A prediction, not a re-implementation: the server
 * measures again and its answer is the one that decides.
 */
export function contentByteSize(parts: readonly DraftPart[]): number {
  const encoder = new TextEncoder();
  let total = 0;
  for (const part of parts) {
    if (part.kind === "prompt_text") {
      total += encoder.encode(JSON.stringify(part.text)).length;
      continue;
    }
    try {
      total += encoder.encode(JSON.stringify(JSON.parse(part.text))).length;
    } catch {
      // Unparseable JSON is reported as its own problem; it contributes its raw
      // length here so an oversized buffer is not hidden behind a syntax error.
      total += encoder.encode(part.text).length;
    }
  }
  return total;
}

export function findPartProblems(parts: readonly DraftPart[]): PartProblem[] {
  const problems: PartProblem[] = [];

  if (parts.length < MIN_PARTS) {
    problems.push({
      index: null,
      message: "An asset version must contain at least one part.",
    });
  }
  if (parts.length > MAX_PARTS) {
    problems.push({
      index: null,
      message: `An asset version may contain at most ${MAX_PARTS} parts.`,
    });
  }

  const seen = new Map<string, number>();
  parts.forEach((part, index) => {
    if (part.key.length === 0) {
      problems.push({ index, message: "This part needs a key." });
    } else if (!PART_KEY_PATTERN.test(part.key)) {
      problems.push({
        index,
        message:
          'A part key is a lowercase slug, optionally prefixed by its language — "screening_prompt" or "he/screening_prompt".',
      });
    } else {
      const first = seen.get(part.key);
      if (first === undefined) {
        seen.set(part.key, index);
      } else {
        problems.push({
          index,
          message: `Part keys must be unique; this key is already used by part ${first + 1}.`,
        });
      }
    }

    if (part.kind === "prompt_text") {
      if (part.text.length === 0) {
        problems.push({ index, message: "A prompt part cannot be empty." });
      } else if (part.text.length > MAX_PROMPT_CHARS) {
        problems.push({
          index,
          message: `A prompt part is at most ${MAX_PROMPT_CHARS.toLocaleString("en-US")} characters; this one is ${part.text.length.toLocaleString("en-US")}.`,
        });
      }
      return;
    }

    try {
      JSON.parse(part.text);
    } catch (error) {
      problems.push({
        index,
        message: `This structured part is not valid JSON: ${error instanceof Error ? error.message : "the document could not be parsed"}.`,
      });
    }
  });

  const size = contentByteSize(parts);
  if (size > MAX_CONTENT_BYTES) {
    problems.push({
      index: null,
      message: `This content is ${size.toLocaleString("en-US")} bytes across ${parts.length} parts; the limit is ${MAX_CONTENT_BYTES.toLocaleString("en-US")}. Store large corpora as pack files.`,
    });
  }

  return problems;
}

/**
 * The draft as wire content, or the reasons it is not sendable yet.
 *
 * Never partially converts: a caller either gets content it may send or a list
 * of problems it may attribute, so no path exists that sends half a document.
 */
export function toPartsContent(
  parts: readonly DraftPart[],
):
  | { ok: true; content: Extract<LibraryAssetContent, { kind: "parts" }> }
  | { ok: false; problems: PartProblem[] } {
  const problems = findPartProblems(parts);
  if (problems.length > 0) {
    return { ok: false, problems };
  }
  return {
    ok: true,
    content: {
      kind: "parts",
      parts: parts.map((part) =>
        part.kind === "prompt_text"
          ? { kind: "prompt_text" as const, key: part.key, text: part.text }
          : {
              kind: "structured" as const,
              key: part.key,
              data: JSON.parse(part.text) as unknown,
            },
      ),
    },
  };
}

/**
 * Dirty is decided on what the clinician typed, not on what it would serialise
 * to.
 *
 * Reformatting a structured part's JSON without changing its meaning counts as
 * a change. That is the honest reading of the control in front of them — the
 * buffer differs from the one they opened — and the alternative, comparing
 * parsed values, would leave a visibly edited textarea reporting "no changes"
 * and a Discard button that appeared to do nothing.
 */
export function isSameDraft(
  left: readonly DraftPart[],
  right: readonly DraftPart[],
): boolean {
  return (
    left.length === right.length &&
    left.every((part, index) => {
      const other = right[index] as DraftPart;
      return (
        part.key === other.key &&
        part.kind === other.kind &&
        part.text === other.text
      );
    })
  );
}

export function movePart(
  parts: readonly DraftPart[],
  from: number,
  to: number,
): DraftPart[] {
  if (from === to || from < 0 || to < 0 || from >= parts.length || to >= parts.length) {
    return [...parts];
  }
  const next = [...parts];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved as DraftPart);
  return next;
}

export function removePart(
  parts: readonly DraftPart[],
  index: number,
): DraftPart[] {
  return parts.filter((_, position) => position !== index);
}

export function updatePart(
  parts: readonly DraftPart[],
  index: number,
  patch: Partial<Pick<DraftPart, "key" | "text">>,
): DraftPart[] {
  return parts.map((part, position) =>
    position === index ? { ...part, ...patch } : part,
  );
}

/**
 * Which parts on screen a server refusal is about.
 *
 * The service points every parts failure at `content.parts` — correct, and too
 * coarse to satisfy this ticket's "attributed to the offending part, not to
 * 'content'". The message itself is more specific than the pointer, and
 * `validate_parts` writes it in two machine-readable shapes:
 *
 *   "… does not define the parts at [0, 2]; it allows: …"   -> exact positions
 *   "part 'screening_prompt' for 'he' … must be prompt_text" -> a quoted role
 *
 * Both are quoted or bracketed by the SERVER around vocabulary the server
 * defined — never around a caller's raw key, which that function is careful
 * never to echo. So this reads structure, not prose: a message in neither shape
 * (the third, "requires the parts for 'he': …") resolves to no part at all,
 * which is right — a missing role has no control on screen to blame, and the
 * refusal stays with the list.
 */
export function attributePartsFailure(
  message: string,
  parts: readonly DraftPart[],
): number[] {
  const positional = /\bparts at \[([\d,\s]*)\]/.exec(message);
  if (positional) {
    return (positional[1] as string)
      .split(",")
      .map((entry) => Number.parseInt(entry.trim(), 10))
      .filter((index) => Number.isInteger(index) && index >= 0 && index < parts.length);
  }

  const role = /\bpart '([a-z][a-z0-9_]{0,63})'/.exec(message);
  if (!role) {
    return [];
  }
  const language = / for '([a-z]{2}(?:-[A-Z]{2})?)'/.exec(message);
  return parts.reduce<number[]>((matches, part, index) => {
    const [partLanguage, partRole] = splitPartKey(part.key);
    if (partRole !== role[1]) {
      return matches;
    }
    if (language && partLanguage !== language[1]) {
      return matches;
    }
    matches.push(index);
    return matches;
  }, []);
}
